/**
 * WebAuthn Utilities
 *
 * Helper functions for WebAuthn passkey registration and authentication.
 * Uses the Web Authentication API (navigator.credentials).
 */

/**
 * Serialized credential creation options from backend
 * (with base64url strings instead of BufferSource)
 */
export interface SerializedPublicKeyCredentialCreationOptions {
  challenge: string;
  rp: PublicKeyCredentialRpEntity;
  user: {
    id: string;
    name: string;
    displayName: string;
  };
  pubKeyCredParams: PublicKeyCredentialParameters[];
  timeout?: number;
  excludeCredentials?: PublicKeyCredentialDescriptor[];
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
}

/**
 * Convert base64url string to ArrayBuffer
 */
function base64urlToBuffer(base64url: string): ArrayBuffer {
  // Replace URL-safe characters
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if needed
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    '='
  );

  // Decode base64 to binary string
  const binary = atob(padded);

  // Convert to ArrayBuffer (efficient method using Uint8Array.from)
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));

  return bytes.buffer;
}

/**
 * Convert ArrayBuffer to base64url string
 */
function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  // Encode to base64
  const base64 = btoa(binary);

  // Convert to base64url (URL-safe)
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Prepare credential creation options for WebAuthn API
 *
 * The backend sends options in a serialized format (base64url strings).
 * We need to convert these to ArrayBuffers for the WebAuthn API.
 */
export function prepareCredentialCreationOptions(
  options: SerializedPublicKeyCredentialCreationOptions
): PublicKeyCredentialCreationOptions {
  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64urlToBuffer(options.user.id),
    },
  };
}

/**
 * Serialize credential for backend
 *
 * Convert ArrayBuffers from the WebAuthn API response to base64url strings
 * for transmission to the backend.
 */
export function serializeCredential(credential: PublicKeyCredential): {
  id: string;
  rawId: string;
  response: {
    clientDataJSON: string;
    attestationObject: string;
  };
  type: 'public-key';
} {
  const response = credential.response as AuthenticatorAttestationResponse;

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      attestationObject: bufferToBase64url(response.attestationObject),
    },
    type: 'public-key',
  };
}

/**
 * Register a passkey using WebAuthn
 *
 * @param options Credential creation options from backend (serialized format)
 * @returns Serialized credential for backend registration
 */
export async function registerPasskey(
  options: SerializedPublicKeyCredentialCreationOptions
): Promise<{
  id: string;
  rawId: string;
  response: {
    clientDataJSON: string;
    attestationObject: string;
  };
  type: 'public-key';
}> {
  // Check if WebAuthn is supported
  if (!window.PublicKeyCredential) {
    throw new Error(
      'WebAuthn is not supported in this browser. Please use a modern browser like Chrome, Safari, or Edge.'
    );
  }

  // Check if platform authenticator is available
  const available =
    await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  if (!available) {
    throw new Error(
      'No biometric authenticator available on this device. Please use a device with Face ID, Touch ID, or Windows Hello.'
    );
  }

  // Prepare options (convert base64url to ArrayBuffer)
  const preparedOptions = prepareCredentialCreationOptions(options);

  // Create credential
  const credential = await navigator.credentials.create({
    publicKey: preparedOptions,
  });

  if (!credential || !(credential instanceof PublicKeyCredential)) {
    throw new Error('Failed to create passkey credential');
  }

  // Serialize credential for backend
  return serializeCredential(credential);
}
